import { useEffect, useRef } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  getAmplitudeDefaultEventProperties,
  getAmplitudeDeviceCategory,
  SIGNUP_FLOW_DE_MOBILE,
} from '../../../utils/amplitudeEvents';
import { TMobileSignupForm } from '../form/form';
import { furthestAllowedIndex } from '../form/resume';
import { useMobileSignupResume } from '../form/useMobileSignupResume';
import { SCREEN_REGISTRY } from '../screens/registry';
import { TScreenDef } from '../screens/types';

export type TNavDirection = 'forwards' | 'backwards';

export type TMobileSignupNav =
  | { status: 'pending' }
  | {
      status: 'ready';
      index: number;
      screen: TScreenDef;
      direction: TNavDirection;
      goNext: () => void;
      goBack?: () => void;
    };

export const useMobileSignupNav = (): TMobileSignupNav => {
  const { hash, search } = useLocation();
  const navigate = useNavigate();
  const { getValues } = useFormContext<TMobileSignupForm>();
  const resume = useMobileSignupResume();

  const screenId = hash.replace(/^#/, '');
  const hashIndex = SCREEN_REGISTRY.findIndex(
    (screen) => screen.id === screenId,
  );
  const lastIndex = SCREEN_REGISTRY.length - 1;

  let resolvedIndex: number;
  let needsRedirect = false;
  if (resume.status === 'complete') {
    // Complete is a ceiling, not a pin: honour any valid hash so the user
    // can navigate back to fix data after a terminal-stage failure. Only an
    // unknown/empty hash falls back to the last screen.
    if (hashIndex === -1) {
      resolvedIndex = lastIndex;
      needsRedirect = true;
    } else {
      resolvedIndex = hashIndex;
    }
  } else {
    const ceiling = furthestAllowedIndex(getValues(), SCREEN_REGISTRY);
    if (hashIndex === -1 || hashIndex > ceiling) {
      resolvedIndex = ceiling;
      needsRedirect = true;
    } else {
      resolvedIndex = hashIndex;
    }
  }

  const targetId = SCREEN_REGISTRY[resolvedIndex]?.id;

  useEffect(() => {
    if (resume.status === 'pending') return;
    if (needsRedirect && targetId) {
      // Carry the query string so staff impersonation (?state_key=) and campaign params survive each step.
      navigate({ search, hash: `#${targetId}` }, { replace: true });
    }
  }, [resume.status, needsRedirect, targetId, search, navigate]);

  // Gated on !needsRedirect so a skip-ahead fires for the ceiling it lands on, not the jumped-to URL.
  // Keyed on the landed index so a resume.status flip that leaves the index unchanged (e.g.
  // 'screen' → 'complete' at the terminal screen) doesn't re-emit and inflate load counts.
  const lastLoadedIndexRef = useRef<number | null>(null);
  useEffect(() => {
    if (resume.status === 'pending' || needsRedirect) return;
    if (lastLoadedIndexRef.current === resolvedIndex) return;
    const landed = SCREEN_REGISTRY[resolvedIndex];
    if (!landed) return;
    lastLoadedIndexRef.current = resolvedIndex;
    tracking.ampli.subStepLoaded({
      sub_step: landed.id,
      stage: landed.stage,
      sub_step_index: resolvedIndex,
      client_device: getAmplitudeDeviceCategory(),
      signup_flow: SIGNUP_FLOW_DE_MOBILE,
      ...getAmplitudeDefaultEventProperties(),
    });
  }, [resume.status, needsRedirect, resolvedIndex]);

  const prevIndexRef = useRef(resolvedIndex);
  const direction: TNavDirection =
    resolvedIndex >= prevIndexRef.current ? 'forwards' : 'backwards';
  // Record this render's index after commit so the next render can compare against it.
  useEffect(() => {
    prevIndexRef.current = resolvedIndex;
  });

  // Once a screen has resolved, a background refetch (post-mint invalidation, or
  // refresh after seeding) must not tear the screen down to the full-page loader —
  // hold on the last known index instead.
  const lastReadyIndexRef = useRef<number | null>(null);

  const navFrom = (index: number) => ({
    goNext: () => {
      const nextId = SCREEN_REGISTRY[index + 1]?.id;
      if (!nextId) return;
      const current = SCREEN_REGISTRY[index];
      tracking.ampli.subStepSubmitted({
        sub_step: current.id,
        stage: current.stage,
        sub_step_index: index,
        client_device: getAmplitudeDeviceCategory(),
        signup_flow: SIGNUP_FLOW_DE_MOBILE,
        ...getAmplitudeDefaultEventProperties(),
      });
      navigate({ search, hash: `#${nextId}` });
    },
    goBack:
      index > 0
        ? () => {
            const prevId = SCREEN_REGISTRY[index - 1]?.id;
            if (prevId) navigate({ search, hash: `#${prevId}` });
          }
        : undefined,
  });

  const readyAt = (index: number): TMobileSignupNav => ({
    status: 'ready',
    index,
    screen: SCREEN_REGISTRY[index],
    direction,
    ...navFrom(index),
  });

  if (resume.status === 'pending') {
    if (lastReadyIndexRef.current === null) {
      return { status: 'pending' };
    }
    return readyAt(lastReadyIndexRef.current);
  }

  lastReadyIndexRef.current = resolvedIndex;

  return readyAt(resolvedIndex);
};
